/**
 * IA (Intelligence Artificielle) Tab - Streamlined Design
 * AI-powered ElementsAppreciationSituation generation using GPT-5
 * Simplified workflow with integrated prompt and history management
 * Includes approval workflow for generated texts
 */

import React, { useState } from 'react'
import { AffaireSelector } from './components/AffaireSelector'
import { GenerationPanel } from './components/GenerationPanel'
import { RecentAffairesQuickSelect } from './components/RecentAffairesQuickSelect'
import { ApprovalWorkflowTab } from './components/ApprovalWorkflowTab'
import { PromptManager } from './components/PromptManager'
import {
  SparklesIcon,
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  LightBulbIcon,
  ArrowUpTrayIcon,
  ClockIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline'
import { useAppreciation, useHistory, useSaveAppreciation, useActivePrompt, useGenerateAppreciation } from './hooks/useIA'
import './styles/IATab.css'

type TabType = 'generation' | 'approvals'
type IAMode = 'selection' | 'appreciation-generation'

export const IATab: React.FC = () => {
  // State for mode selection (top level navigation)
  const [selectedMode, setSelectedMode] = useState<IAMode | null>(null)

  // State for tab navigation
  const [activeTab, setActiveTab] = useState<TabType>('generation')

  // State for selected affaire
  const [selectedAffaireId, setSelectedAffaireId] = useState<number | null>(null)
  const [selectedAffaireAdresse, setSelectedAffaireAdresse] = useState<string | null>(null)
  const [isEditingPrompt, setIsEditingPrompt] = useState(false)

  // State for tracking ongoing generations (persists across tab switches)
  const [generatingAffaires, setGeneratingAffaires] = useState<Set<number>>(new Set())

  // Fetch appreciation and history data
  const { data: appreciationData } = useAppreciation(selectedAffaireId)
  const { data: historyData = [] } = useHistory(selectedAffaireId)
  const { data: activePrompt } = useActivePrompt()
  const saveMutation = useSaveAppreciation()
  const generateMutation = useGenerateAppreciation()

  // Get the most recent generated text from history
  const lastGenerated = historyData.length > 0 ? historyData[0] : null
  const hasAppreciation = appreciationData?.appreciation && appreciationData.appreciation.trim().length > 0

  const handleAffaireSelect = (affaireId: number, adresse: string) => {
    setSelectedAffaireId(affaireId)
    setSelectedAffaireAdresse(adresse)
  }

  const resetSelection = () => {
    setSelectedAffaireId(null)
    setSelectedAffaireAdresse(null)
  }

  // Handle quick-select generation (without opening affaire)
  const handleQuickGenerate = async (affaireId: number, adresse: string) => {
    if (!activePrompt) {
      return
    }

    // Add to generating set
    setGeneratingAffaires(prev => new Set(prev).add(affaireId))

    try {
      // Auto-generate with active prompt WITHOUT opening the affaire
      const fullPrompt = `${activePrompt.promptText}\n\nAdresse du bien: ${adresse}`

      await generateMutation.mutateAsync({
        affaireId,
        fullPrompt,
        promptId: activePrompt.id
      })
    } finally {
      // Remove from generating set
      setGeneratingAffaires(prev => {
        const newSet = new Set(prev)
        newSet.delete(affaireId)
        return newSet
      })
    }
  }

  // Handle opening an affaire from quick-select or approval tab
  const handleOpenAffaire = (affaireId: number, adresse: string) => {
    setSelectedAffaireId(affaireId)
    setSelectedAffaireAdresse(adresse)
    // Switch to generation tab if not already there
    if (activeTab !== 'generation') {
      setActiveTab('generation')
    }
  }

  // Show mode selection if no mode is selected
  if (!selectedMode) {
    return (
      <div className="pipeline-page-container">
        {/* Header Section */}
        <div className="ia-header">
          <div className="ia-header-content">
            <h1 className="ia-title">
              <SparklesIcon className="ia-title-icon" />
              Intelligence Artificielle
            </h1>
            <p className="ia-subtitle">
              Sélectionnez le type d'assistance IA dont vous avez besoin
            </p>
          </div>
        </div>

        {/* Mode Selection Cards */}
        <div className="ia-content-wrapper">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Appreciation Generation Mode */}
            <button
              onClick={() => setSelectedMode('appreciation-generation')}
              className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-8 hover:border-indigo-400 hover:shadow-xl transition-all duration-200 text-left group"
            >
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-indigo-500 to-purple-600 group-hover:scale-110 transition-transform duration-200">
                  <SparklesIcon className="h-8 w-8 text-white" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-indigo-600 transition-colors">
                    Générer une description de la situation
                  </h2>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Utilisez l'IA pour générer automatiquement des appréciations de situation détaillées et professionnelles pour vos affaires.
                  </p>
                  <div className="mt-4 flex items-center gap-2 text-sm font-medium text-indigo-600">
                    <span>Commencer</span>
                    <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </div>
            </button>

            {/* Future Mode - Placeholder */}
            <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-8 opacity-50 cursor-not-allowed text-left">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-gray-400 to-gray-500">
                  <ClipboardDocumentListIcon className="h-8 w-8 text-white" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-gray-900 mb-2">
                    Autres fonctionnalités IA
                  </h2>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    D'autres outils d'assistance IA seront bientôt disponibles.
                  </p>
                  <div className="mt-4 text-sm font-medium text-gray-400">
                    Bientôt disponible
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="pipeline-page-container">
      {/* Header Section with Back Button */}
      <div className="ia-header">
        <div className="ia-header-content">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="ia-title">
                <SparklesIcon className="ia-title-icon" />
                Intelligence Artificielle
              </h1>
              <p className="ia-subtitle">
                Génération automatique d'appréciations de situation avec GPT-5
              </p>
            </div>
            <button
              onClick={() => {
                setSelectedMode(null)
                setSelectedAffaireId(null)
                setSelectedAffaireAdresse(null)
                setIsEditingPrompt(false)
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border-2 border-gray-300 hover:bg-gray-50 rounded-lg transition-all duration-150"
            >
              ← Retour aux modes
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200 mb-6">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('generation')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-all duration-150 ${
                activeTab === 'generation'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <SparklesIcon className="w-5 h-5" />
                Génération
              </div>
            </button>

            <button
              onClick={() => setActiveTab('approvals')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-all duration-150 ${
                activeTab === 'approvals'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <ClockIcon className="w-5 h-5" />
                Approbations
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="ia-content-wrapper">
        {activeTab === 'generation' ? (
          /* Generation Tab */
          <>
            {!isEditingPrompt && (
              <>
                {selectedAffaireId ? (
                  /* Compact header when affaire is selected */
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 overflow-hidden mb-6">
                    <div className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#244886' }}>
                          <LightBulbIcon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-700">Affaire sélectionnée</p>
                          <p className="text-sm font-bold text-gray-900">{selectedAffaireAdresse || `#${selectedAffaireId}`}</p>
                        </div>
                      </div>
                      <button
                        onClick={resetSelection}
                        className="text-xs px-3 py-1.5 rounded-lg border font-semibold hover:bg-blue-100 transition-colors"
                        style={{ borderColor: '#244886', color: '#244886' }}
                      >
                        Changer
                      </button>
                    </div>

                    {/* Appreciation Status */}
                    {hasAppreciation ? (
                      <div className="px-4 pb-4">
                        <div className="bg-white border rounded-lg p-3 flex items-center gap-2" style={{ borderColor: '#10B981' }}>
                          <CheckCircleIcon className="h-5 w-5 flex-shrink-0" style={{ color: '#10B981' }} />
                          <div className="flex-1">
                            <p className="text-xs font-semibold" style={{ color: '#10B981' }}>Appréciation enregistrée</p>
                            <p className="text-xs text-gray-600 mt-0.5">Une appréciation existe déjà dans FileMaker</p>
                          </div>
                        </div>
                      </div>
                    ) : lastGenerated ? (
                      <div className="px-4 pb-4">
                        <div className="bg-white border border-yellow-300 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <SparklesIcon className="h-5 w-5 text-yellow-600 flex-shrink-0" />
                              <p className="text-xs font-semibold text-yellow-800">Dernière génération non sauvegardée</p>
                            </div>
                            <button
                              onClick={async () => {
                                if (selectedAffaireId && lastGenerated.generatedText) {
                                  await saveMutation.mutateAsync({
                                    affaireId: selectedAffaireId,
                                    appreciationText: lastGenerated.generatedText
                                  })
                                }
                              }}
                              disabled={saveMutation.isPending}
                              className="text-xs px-3 py-1.5 rounded-lg font-semibold text-white flex items-center gap-1.5 disabled:opacity-50"
                              style={{ backgroundColor: '#244886' }}
                            >
                              <ArrowUpTrayIcon className="h-3.5 w-3.5" />
                              {saveMutation.isPending ? 'Envoi...' : 'Envoyer à FileMaker'}
                            </button>
                          </div>
                          <p className="text-xs text-gray-600 line-clamp-2">{lastGenerated.generatedText}</p>
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  /* Search interface when no affaire selected */
                  <div className="mb-6">
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6 mb-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#244886' }}>
                            <SparklesIcon className="h-7 w-7 text-white" />
                          </div>
                          <div>
                            <h2 className="text-lg font-bold text-gray-900 mb-1">Génération d'appréciation avec IA</h2>
                            <p className="text-sm text-gray-700">
                              Sélectionnez une affaire récente ou recherchez manuellement pour générer automatiquement une appréciation de situation avec GPT-5
                            </p>
                          </div>
                        </div>

                        {/* Edit Prompt Button */}
                        <button
                          onClick={() => setIsEditingPrompt(true)}
                          className="flex items-center gap-2 px-4 py-2 bg-white text-indigo-600 text-sm font-medium rounded-lg border-2 border-indigo-200 hover:bg-indigo-50 hover:border-indigo-300 active:bg-indigo-100 transition-all duration-150 shadow-sm flex-shrink-0"
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                          Modifier le prompt
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Main Content Area */}
            <div className="space-y-6">
              {/* Prompt Manager - Shown when editing prompt */}
              {isEditingPrompt && !selectedAffaireId && (
                <div className="space-y-4">
                  {/* Header with back button */}
                  <div className="bg-white rounded-2xl shadow-md border-2 border-gray-200 p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                          <PencilSquareIcon className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div>
                          <h2 className="text-lg font-semibold text-gray-900">
                            Gestion des prompts
                          </h2>
                          <p className="text-sm text-gray-600">
                            Créer, modifier et gérer les prompts IA
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => setIsEditingPrompt(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all duration-150"
                      >
                        Retour
                      </button>
                    </div>
                  </div>

                  {/* Prompt Manager Component */}
                  <PromptManager
                    onPromptSelect={() => {}}
                    selectedPromptId={activePrompt?.id || null}
                  />
                </div>
              )}

              {/* Affaire Selection - First, for manual search */}
              {!isEditingPrompt && !selectedAffaireId && (
                <AffaireSelector
                  selectedAffaireId={selectedAffaireId}
                  onAffaireSelect={handleAffaireSelect}
                  onGenerateClick={handleQuickGenerate}
                  generatingAffaires={generatingAffaires}
                />
              )}

              {/* Recent Affaires Quick Select - Second, for quick generation */}
              {!isEditingPrompt && !selectedAffaireId && (
                <RecentAffairesQuickSelect
                  onGenerateClick={handleQuickGenerate}
                  onOpenAffaire={handleOpenAffaire}
                  generatingAffaires={generatingAffaires}
                />
              )}

              {/* Main Workflow - Only shown when affaire selected */}
              {selectedAffaireId && (
                <GenerationPanel
                  affaireId={selectedAffaireId}
                  adresse={selectedAffaireAdresse}
                  onEditingStateChange={setIsEditingPrompt}
                />
              )}
            </div>
          </>
        ) : (
          /* Approvals Tab */
          <ApprovalWorkflowTab onOpenAffaire={handleOpenAffaire} />
        )}
      </div>
    </div>
  )
}
